import { KeyboardArrowDown } from "@mui/icons-material";
import { AutocompleteValue, createFilterOptions, FilterOptionsState, Stack, TextField } from "@mui/material";
import MuiAutocomplete from "@mui/material/Autocomplete";
import { IAutocompleteProps, IAutocompleteValue, NestedKeyOf } from "components/inputs/Autocomplete/types";
import { get, set } from "lodash";
import React from "react";
import FieldLabel from "../../FieldLabel";
import getInputVariantSx from "../inputStyles";

const Autocomplete = <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Value extends Record<string, any> | string = IAutocompleteValue,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TOption extends Record<string, any> | string = Value,
>({
  options = [],
  optionName = "displayName" as NestedKeyOf<Value>,
  multiple,
  disabled,
  onInputChange,
  placeholder,
  equalityParam = "id",
  searchParam = "id",
  loading,
  getOptionLabel,
  freeSolo,
  label,
  labelLink,
  value,
  error,
  onChange,
  onChangeTextField,
  filterOptionsParams,
  formatDataBeforeOnChange,
  renderOption,
  sx,
  stackSx,
  required,
  variant,
  hint,
  noOptionsText = "Нет данных",
}: IAutocompleteProps<Value, Multiple, DisableClearable, FreeSolo, TOption>) => {
  const filter = createFilterOptions<Value | TOption>({
    stringify: (option) => (typeof option === "string" ? option : (get(option, searchParam) as string)),
  });

  return (
    <Stack
      spacing={0.5}
      sx={{ ...stackSx }}>
      {!!label && (
        <FieldLabel
          label={label}
          disabled={disabled}
          labelLink={labelLink}
          required={required}
        />
      )}
      <MuiAutocomplete<Value | TOption, Multiple, DisableClearable, FreeSolo>
        size='small'
        value={value}
        multiple={multiple}
        options={options || []}
        filterOptions={(existingOptions: (Value | TOption)[], params: FilterOptionsState<Value | TOption>) => {
          const currentParams = filterOptionsParams ? filterOptionsParams(params) : params;
          const filtered = filter(existingOptions, currentParams);

          if (freeSolo && params.inputValue !== "") {
            const isExisting = existingOptions.some(
              (option) => params.inputValue === get(option, optionName as string)
            );

            if (!isExisting && !loading) {
              const newOption = set(
                { newOption: `Добавить ${params.inputValue}` },
                optionName as string,
                params.inputValue
              );
              filtered.push(newOption as unknown as Value);
            }
          }

          return filtered;
        }}
        disabled={disabled}
        loading={loading}
        freeSolo={freeSolo}
        loadingText='Загрузка...'
        noOptionsText={noOptionsText}
        getOptionLabel={(option) => {
          if (getOptionLabel) return getOptionLabel(option);

          if (typeof option === "object") return get(option, optionName) as string;
          return option;
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder || label}
            helperText={error?.message || hint}
            error={!!error}
            variant={variant}
            sx={getInputVariantSx(variant)}
            inputProps={{
              ...params.inputProps,
              onChange: (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                if (onChangeTextField) {
                  const newEvent = onChangeTextField(event);
                  params.inputProps.onChange?.(newEvent);
                } else params.inputProps.onChange?.(event as React.ChangeEvent<HTMLInputElement>);
              },
            }}
          />
        )}
        popupIcon={<KeyboardArrowDown sx={{ color: "base.200", width: 24, height: 24 }} />}
        renderOption={
          renderOption
            ? (optionProps, option, state, ownerState) => renderOption(optionProps, option, state, ownerState)
            : (optionProps, option) => {
                let text;
                if (typeof option === "object" && "newOption" in option) {
                  text = option.newOption;
                } else if (getOptionLabel) {
                  text = getOptionLabel(option);
                }
                if (typeof option === "string") {
                  text = option;
                } else {
                  text = get(option, optionName as string) as string;
                }
                return (
                  <li
                    {...optionProps}
                    key={typeof option === "string" ? option : `${get(option, searchParam as string)}-${text}`}>
                    {text}
                  </li>
                );
              }
        }
        clearOnBlur
        isOptionEqualToValue={(option, inputValue) => {
          const optionData = typeof option === "string" ? option : get(option, equalityParam as string);
          const inputData = typeof inputValue === "string" ? inputValue : get(inputValue, equalityParam as string);
          return optionData === inputData;
        }}
        onChange={(_, data, reason) => {
          if (reason === "clear") {
            onChange(
              (multiple ? [] : null) as AutocompleteValue<Value | TOption, Multiple, DisableClearable, FreeSolo>
            );
          } else {
            const newData = formatDataBeforeOnChange ? formatDataBeforeOnChange(data) : data;
            onChange(newData);
          }
        }}
        onInputChange={onInputChange}
        sx={getInputVariantSx(variant, sx)}
      />
    </Stack>
  );
};

export default Autocomplete;
